/**
 * Time-boxed support access (droidmap#2651): DroidMap support's access to a
 * customer's user, org or project, for a while and for a reason.
 *
 * Kept apart from {@link Grant}: it has one principal (DroidMap support), no
 * role in the customer's role model, and may name a user who belongs to no
 * organisation.
 *
 * Staff do not need a customer's permission to start a session: each one
 * makes its own grant, for its own 30 minutes, and ending the session
 * revokes it. A help request still makes one too, as the record of where the
 * user asked for help; it gates nothing.
 */

/** What a support access grant is on. */
export enum SupportAccessResourceType {
  USER = 'user',
  ORGANISATION = 'organisation',
  PROJECT = 'project',
}

/** What the access allows. */
export enum SupportAccessKind {
  /** See the app as the user sees it, as the user, read-only. On a user. */
  OBSERVE = 'observe',
  /**
   * Act in an organisation as DroidMap support, as its admin, never as a
   * user. On an organisation.
   */
  ASSIST = 'assist',
}

/** Where a grant came from. */
export enum SupportAccessSource {
  /**
   * The user asked for help. The record of where and on which build, which
   * the console shows beside a session; it gates nothing.
   */
  HELP_REQUEST = 'help_request',
  /** A staff member started a session, on a reason: the session's own grant. */
  STAFF = 'staff',
}

/**
 * DroidMap support, as the principal an assist session acts as. Not a user
 * row: a fixed id the permissions service holds grants for while an assist
 * session is live in an org, so every change it makes is attributed to
 * DroidMap support (and, in event metadata, to the staff member).
 */
export const SUPPORT_USER_ID = '00000000000000000000DMSPRT'

/** How long a help request is shown to staff as context for a session. */
export const HELP_REQUEST_SUPPORT_ACCESS_DAYS = 7

/** How long a session's own grant lasts: the session. */
export const SESSION_SUPPORT_ACCESS_MS = 30 * 60 * 1000

export type SupportAccessGrant = {
  id: string
  resourceType: SupportAccessResourceType
  resourceId: string
  orgId?: string
  access: SupportAccessKind
  source: SupportAccessSource
  /** The help request's report id, or the grant's own id. */
  ref: string
  /** The staff member's reason, for a session's grant; absent otherwise. */
  reason?: string
  /** The help request's route and build, shown to staff before a session. */
  route?: string
  buildSha?: string
  /** `system` for a help request; `staff:<sub>` for a session's. */
  grantedBy: string
  grantedAt: string
  expiresAt: string
}

export type CreateSupportAccessGrantInput = Omit<
  SupportAccessGrant,
  'id' | 'grantedAt' | 'expiresAt' | 'ref'
> & {
  /** Defaults to the new grant's id. */
  ref?: string
  /** Milliseconds from now the grant lasts. */
  durationMs: number
}

/**
 * What the internal grant route takes: the grant, and the admin service's
 * signed assertion that it asked for exactly this grant (droidmap#2651).
 */
export type CreateSupportAccessGrantRequest = CreateSupportAccessGrantInput & {
  assertion: string
}

export type ListSupportAccessGrantsInput = {
  resourceType: SupportAccessResourceType
  resourceId: string
}
